"use client";

/* eslint-disable tailwindcss/no-custom-classname */

import Script from "next/script";
import React, { useEffect, useState } from "react";

interface ColumnData {
  line1: string;
  line2: string;
}

interface GoogleRatingAndProceduresProps {
  googleReviewsWidgetId?: string;
  // Legacy props for backward compatibility
  proceduresCount?: string;
  proceduresLabel?: string;
  // New column structure
  column2?: ColumnData;
  column3?: ColumnData;
}

export default function GoogleRatingAndProcedures({
  googleReviewsWidgetId = "5d1bb2c3-a8ff-4600-b9ee-a5456d8b4801",
  proceduresCount,
  proceduresLabel,
  column2,
  column3,
}: GoogleRatingAndProceduresProps) {
  // Backward compatibility: if column2 is not provided but proceduresCount is, use it
  // If neither is provided, use default values
  const column2Data: ColumnData | undefined =
    column2 ||
    (proceduresCount && proceduresLabel
      ? { line1: proceduresCount, line2: proceduresLabel }
      : undefined);

  // Determine number of columns (always at least 2: Google Rating + Column 2)
  // Check if column3 exists and has valid data
  const hasColumn3 = !!(column3 && typeof column3 === 'object' && column3.line1 && column3.line2);
  const hasColumn2 = !!(column2Data && column2Data.line1 && column2Data.line2);
  
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    const scriptId = "elfsight-platform-script";
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://elfsightcdn.com/platform.js";
    script.async = true;
    script.onload = () => setScriptLoaded(true);

    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    // Add custom CSS to ensure Elfsight widget respects flex layout
    if (scriptLoaded) {
      const styleId = `elfsight-widget-style-${googleReviewsWidgetId}`;
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .elfsight-app-${googleReviewsWidgetId} {
            max-width: 100% !important;
            width: 100% !important;
            flex: 1 1 0% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .elfsight-app-${googleReviewsWidgetId} > * {
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, [scriptLoaded, googleReviewsWidgetId]);

  const columns = [
    { type: "google", data: null },
    ...(hasColumn2 && column2Data ? [{ type: "custom", data: column2Data }] : []),
    ...(hasColumn3 && column3 ? [{ type: "custom", data: column3 }] : []),
  ];
  
  // Debug: Log to help troubleshoot
  if (process.env.NODE_ENV === 'development') {
    console.log('GoogleRatingAndProcedures Debug:', {
      receivedColumn2: column2,
      receivedColumn3: column3,
      column2Data,
      hasColumn2,
      hasColumn3,
      columnsCount: columns.length,
      columns,
    });
  }

  return (
    <div>
      <div
        className={`flex items-center gap-2 md:gap-3 lg:max-w-2xl lg:gap-2 ${
          hasColumn3 ? "flex-row" : "flex-row"
        }`}
      >
        {columns.map((column, index) => {
          const isLast = index === columns.length - 1;
          return (
            <React.Fragment key={index}>
              {/* Column Content */}
              {column.type === "google" ? (
                <div className="flex min-w-0 flex-1 justify-center lg:min-w-0 lg:flex-none lg:justify-start">
                  <div
                    className={`elfsight-app-${googleReviewsWidgetId}`}
                    data-elfsight-app-lazy
                  />
                  {!scriptLoaded && (
                    <Script
                      src="https://elfsightcdn.com/platform.js"
                      strategy="lazyOnload"
                      onLoad={() => setScriptLoaded(true)}
                    />
                  )}
                </div>
              ) : (
                <div className="flex min-w-0 flex-1 items-center justify-center lg:min-w-0 lg:flex-none lg:justify-start lg:pl-2">
                  <div className="text-center">
                    <div className="text-lg font-bold text-violet-950 md:text-xl lg:text-2xl">
                      {column.data?.line1}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-black md:text-sm lg:text-base">
                      {column.data?.line2}
                    </div>
                  </div>
                </div>
              )}

              {/* Vertical Divider - only show between columns, not after last */}
              {!isLast && (
                <div className="px-6 flex-shrink-0">
                  <div className="h-12 w-px bg-gray-300 md:h-14 lg:h-16 lg:self-stretch" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
